import { useState, useEffect } from "react";
import { Shield, X } from "lucide-react";
import { useLang } from "@/i18n";

const STORAGE_KEY = "fora_nda_acknowledged";

export const NdaBanner = () => {
  const { t } = useLang();
  const [acknowledged, setAcknowledged] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === "1";
  });

  // Re-check on mount in case user is returning after logout/login cycle
  useEffect(() => {
    setAcknowledged(localStorage.getItem(STORAGE_KEY) === "1");
  }, []);

  const handleAck = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setAcknowledged(true);
  };

  if (acknowledged) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#000000]/85 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
      data-testid="nda-banner"
      role="alertdialog"
      aria-labelledby="nda-banner-title"
      aria-describedby="nda-banner-body"
    >
      <div className="bg-white border border-[#e8e8e8] rounded-sm w-full max-w-2xl overflow-hidden">
        <div className="bg-[#000000] px-6 py-5 flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#dd260e]" strokeWidth={2} />
          <p
            id="nda-banner-title"
            className="font-condensed text-lg font-black uppercase tracking-tight text-white"
          >
            {t("Confidentiality Acknowledgement")}
          </p>
        </div>
        <div className="p-6 sm:p-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-3">
            {t("Internal Non-Disclosure Notice")}
          </p>
          <p id="nda-banner-body" className="text-sm text-[#000000] leading-relaxed mb-3">
            {t("By entering this platform, you confirm that you are an")}{" "}
            <strong>{t("authorized user")}</strong> {t("and agree to keep all content")}{" "}
            <strong>{t("strictly confidential")}</strong>.
          </p>
          <p className="text-sm text-[#000000] leading-relaxed mb-5">
            {t("All materials — including products, case studies, and implementation guidance — are internal and may not be shared externally without written approval. Any unauthorized disclosure may result in disciplinary action.")}
          </p>
          <ul className="text-xs text-[#555555] leading-relaxed space-y-1.5 mb-6 pl-1">
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("For authorized Coca-Cola HBC and Master F&B personnel only.")}</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("Do not screenshot, forward, or share with external parties.")}</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#dd260e] shrink-0">—</span>
              <span>{t("Written approval required before external publication of any content.")}</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={handleAck}
            className="w-full bg-[#000000] hover:bg-[#dd260e] text-white py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm transition-colors"
            data-testid="nda-banner-acknowledge"
          >
            {t("I agree & continue")}
          </button>
          <p className="text-[10px] text-[#555555]/70 text-center mt-3">
            {t("This acknowledgement is stored on this device.")}
          </p>
        </div>
      </div>
    </div>
  );
};
